import { useEffect, useLayoutEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { installExtensionBridge } from '../extensionBridge';
import { extensionBridgeStore } from '../store';
import type { ExtensionScreenPlacements } from '../types';

const useIsomorphicLayoutEffect: typeof useLayoutEffect =
	typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Manages the CSP-safe extension control bridge for the lifetime of the Post
 * Office screen tree, and mirrors on-screen placements for DevTools. Effects
 * do not run during SSR, so this adds no server-side rendering structure.
 *
 * Installs before paint so headless fetches in the same tree can read
 * `window.postOffice.extensionControl` on first paint.
 */
export const useExtensionBridge = (placements: ExtensionScreenPlacements): void => {
	const isExtensionControlEnabled = fg('post-office_enable_extension-control');

	useIsomorphicLayoutEffect(() => {
		if (!isExtensionControlEnabled) {
			return;
		}

		// Exposes the gated, page-owned bridge for the DevTools extension to read
		// placement state and configure simulations without fetch interception.
		const cleanup = installExtensionBridge();

		return () => {
			// Removes only the bridge installed by this hook instance and clears the
			// screen snapshot so an unmounted screen cannot be observed by DevTools.
			cleanup();
			extensionBridgeStore.resetScreenPlacementsForExtension();
		};
	}, [isExtensionControlEnabled]);

	useEffect(() => {
		if (!isExtensionControlEnabled) {
			return;
		}

		extensionBridgeStore.setScreenPlacementsForExtension(placements);
	}, [isExtensionControlEnabled, placements]);
};
